import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Button, Form, Spinner } from "react-bootstrap";
import ExpertPickerForm from "./ExpertPickerForm";
import { searchExperts } from "./api";

// The two built-in ways this widget knows how to find experts. This is NOT a
// prop — ExpertPicker is a tailored Expert-finding widget, not a generic
// configurable search component. A sibling RecipientPicker (#3143) will be its
// own component with its own modes; if a genuinely shared shell emerges later
// it gets extracted then, with concrete requirements.
const SEARCH_MODES = Object.freeze(["semantic", "by_name"]);

const SEARCH_DEBOUNCE_MS = 350;
const SUGGEST_LIMIT = 3;
// Buffer over SUGGEST_LIMIT so the already-selected filter still leaves us a
// fighting chance of SUGGEST_LIMIT hits to return.
const SUGGEST_FETCH_LIMIT = SUGGEST_LIMIT + 5;
// Defaults lean permissive so the picker returns plenty initially and the
// admin narrows down rather than starting from an empty/sparse list:
//   - withinDistance 0.85: wide semantic net; the admin scrolls and adds the
//     ones that fit (tighten via the slider for a stricter set).
//   - loginRecency "any": with 1.month, search often returns nothing for
//     fresh queries (most users haven't logged in recently in dev/seed
//     data), making the picker feel broken. Any-time is more forgiving.
const DEFAULT_FILTERS = Object.freeze({ withinDistance: 0.85, loginRecency: "any" });

const SUGGEST_NO_MATCHES_MESSAGE =
  'No matches found. Try different text, or click "Advanced" for filters / by-name search.';

// ExpertPicker is a search-only widget. The parent owns the selected experts
// (and their hydration); the picker runs searches and emits intent callbacks.
//
// Two VIEW modes (internal state, not parent-controlled — no parent has a
// reason to drive this):
//   - simplified (default): single text field + "Suggest 3 experts" button.
//     One-shot search; on success the picker calls onSuggest([top3]). An
//     "Advanced" link reveals the full picker chrome.
//   - advanced: the tabbed form (By text / By name) with filters and
//     per-result Add buttons.
//
// Within simplified view there are two sub-states gated by whether the parent
// has any selection yet (see SIMPLIFIED-WHEN-EMPTY policy below). Note: the
// picker emits *intent* (onAdd / onSuggest / onClear). It does NOT decide
// replace-vs-append or the max cap — that merge policy belongs to the parent
// (which owns the selection state). This keeps future behaviour changes (e.g.
// "suggest appends instead of replaces") a parent-side one-liner.
//
// Props:
//   selectedExperts: the parent's current selection (array of expert objects,
//                    each with at least `id`). Drives BOTH the result filter
//                    (don't show already-selected) AND the simplified
//                    sub-state. Single honest signal — no separate excludeIds.
//   atMax:           when true, advanced mode's per-row Add buttons disabled.
//   onAdd(expert):   user added one expert from an advanced-mode result row.
//   onSuggest(list): user ran "Suggest" — here are the top hits.
//   onClear():       user clicked Reset — clear the selection.
//   initialFilters:  optional overrides for default filters.
export default function ExpertPicker({
  initialFilters = DEFAULT_FILTERS,
  selectedExperts = [],
  atMax = false,
  onAdd,
  onSuggest,
  onClear,
}) {
  const [viewMode, setViewMode] = useState("simplified"); // "simplified" | "advanced"
  const [searchMode, setSearchMode] = useState(SEARCH_MODES[0]); // advanced tabs

  // Per-tab query state so switching tabs in advanced mode preserves what the
  // admin typed in each. The simplified-mode field shares state with the
  // By-text tab so switching viewModes also preserves the text.
  const [textQuery, setTextQuery] = useState("");
  const [nameQuery, setNameQuery] = useState("");

  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS, ...initialFilters });

  // Advanced-mode debounced search state.
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [retryAdvised, setRetryAdvised] = useState(false);

  // Suggest-specific state. Kept separate from the advanced-mode debounced
  // search so the two pipelines don't interfere — Suggest is one-shot on
  // button click, advanced is debounced on every keystroke.
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestError, setSuggestError] = useState(null);
  // Holds the in-flight Suggest request so it can be cancelled if the admin
  // re-suggests, switches to advanced, or the component unmounts.
  const suggestAbortRef = useRef(null);

  const activeQuery = searchMode === "semantic" ? textQuery : nameQuery;
  // setTextQuery / setNameQuery are stable; wrap the per-mode pick so the prop
  // passed to ExpertPickerForm keeps a stable identity per searchMode.
  const setActiveQuery = useCallback(
    (v) => (searchMode === "semantic" ? setTextQuery(v) : setNameQuery(v)),
    [searchMode],
  );

  // SIMPLIFIED-WHEN-EMPTY policy: today the simplified sub-state shows the
  // search-and-suggest input only while the parent has no selection, and
  // Reset/Advanced links once it does. This is a UI policy, not a contract
  // invariant — if we later want simplified mode to stay available and have
  // Suggest *append*, this single derivation is the place to change (the
  // contract — selectedExperts in, intent callbacks out — does not change).
  const hasSelections = selectedExperts.length > 0;

  const excludeSet = useMemo(() => new Set(selectedExperts.map((e) => e.id)), [selectedExperts]);

  // Debounced search — only runs in advanced view. In simplified view the
  // admin's typing doesn't fire searches; only clicking "Suggest" does.
  useEffect(() => {
    if (viewMode !== "advanced") return undefined;
    if (!activeQuery || activeQuery.trim().length === 0) {
      setResults([]);
      setError(null);
      return undefined;
    }

    const controller = new AbortController();
    const handle = setTimeout(async () => {
      setLoading(true);
      setError(null);
      setRetryAdvised(false);
      try {
        const data = await searchExperts({
          mode: searchMode,
          query: activeQuery.trim(),
          withinDistance: filters.withinDistance,
          loginRecency: filters.loginRecency,
          signal: controller.signal,
        });
        setResults(data);
      } catch (err) {
        if (err.name === "AbortError") return;
        setError(err.message || "Search failed");
        setRetryAdvised(Boolean(err.retryAdvised));
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      clearTimeout(handle);
      controller.abort();
    };
  }, [viewMode, activeQuery, searchMode, filters.withinDistance, filters.loginRecency]);

  // Cancel any in-flight Suggest when leaving simplified view or unmounting,
  // so a late response can't call onSuggest after the admin has moved on.
  useEffect(() => {
    if (viewMode === "advanced") suggestAbortRef.current?.abort();
  }, [viewMode]);
  useEffect(() => () => suggestAbortRef.current?.abort(), []);

  const retrySearch = () => {
    // Bump active query to itself to retrigger the search effect.
    setActiveQuery((q) => `${q}`);
  };

  const visibleResults = useMemo(() => results.filter((r) => !excludeSet.has(r.id)), [results, excludeSet]);

  const handleAdd = useCallback(
    (expert) => {
      if (onAdd) onAdd(expert);
    },
    [onAdd],
  );

  // "Suggest": one-shot semantic search; hand the top hits to the parent via
  // onSuggest. The parent owns whether that replaces or appends and the max
  // cap. Cancels any previous in-flight Suggest first (abort-and-recreate
  // doubles as the re-entry guard, so no suggestLoading dependency needed).
  const handleSuggest = useCallback(async () => {
    const q = textQuery.trim();
    if (!q) return;

    suggestAbortRef.current?.abort();
    const controller = new AbortController();
    suggestAbortRef.current = controller;

    setSuggestLoading(true);
    setSuggestError(null);
    try {
      const data = await searchExperts({
        mode: "semantic",
        query: q,
        withinDistance: filters.withinDistance,
        loginRecency: filters.loginRecency,
        limit: SUGGEST_FETCH_LIMIT,
        signal: controller.signal,
      });
      // Defensive: filter already-selected. Under the current
      // SIMPLIFIED-WHEN-EMPTY policy the input is only shown with an empty
      // selection so this is a no-op, but it keeps the behaviour correct if
      // that policy changes (simplified-with-selections + append).
      const top = data.filter((r) => !excludeSet.has(r.id)).slice(0, SUGGEST_LIMIT);
      if (top.length === 0) {
        setSuggestError(SUGGEST_NO_MATCHES_MESSAGE);
        return;
      }
      if (onSuggest) onSuggest(top);
    } catch (err) {
      if (err.name === "AbortError") return;
      setSuggestError(err.message || "Search failed");
    } finally {
      // Only the latest request should clear the spinner; an aborted older
      // request must not.
      if (suggestAbortRef.current === controller) {
        setSuggestLoading(false);
        suggestAbortRef.current = null;
      }
    }
  }, [textQuery, filters.withinDistance, filters.loginRecency, excludeSet, onSuggest]);

  const handleReset = useCallback(() => {
    if (onClear) onClear();
    setSuggestError(null);
    // Stay in simplified view; admin can re-type and re-Suggest.
  }, [onClear]);

  // ---------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------

  if (viewMode === "simplified") {
    return (
      <div className="expert-picker mt-3">
        {hasSelections ? (
          // Selections present: no input field. Reset clears the selection
          // (returns the picker to the empty-selection simplified state);
          // Advanced opens the full picker for manual refinement.
          <div className="d-flex gap-3 align-items-center">
            <Button variant="link" size="sm" className="p-0" onClick={handleReset}>
              Reset
            </Button>
            <Button variant="link" size="sm" className="p-0" onClick={() => setViewMode("advanced")}>
              Advanced
            </Button>
          </div>
        ) : (
          // No selections yet: simplified search-and-suggest UX.
          <>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Describe the kind of expert you want (e.g. AI regulation in healthcare)"
              value={textQuery}
              onChange={(e) => {
                setTextQuery(e.target.value);
                if (suggestError) setSuggestError(null);
              }}
              className="mb-2"
            />
            <div className="d-flex gap-3 align-items-center">
              <Button
                size="sm"
                variant="primary"
                onClick={handleSuggest}
                disabled={!textQuery.trim() || suggestLoading}
              >
                {suggestLoading ? (
                  <>
                    <Spinner size="sm" animation="border" className="me-1" /> Suggesting…
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-wand-magic-sparkles me-1" aria-hidden="true" />
                    Suggest 3 experts
                  </>
                )}
              </Button>
              <Button variant="link" size="sm" className="p-0" onClick={() => setViewMode("advanced")}>
                Advanced
              </Button>
            </div>
            {suggestError && <div className="text-danger small mt-2">{suggestError}</div>}
          </>
        )}
      </div>
    );
  }

  // Advanced view: the existing full picker chrome.
  return (
    <div className="expert-picker mt-3">
      <ExpertPickerForm
        searchMode={searchMode}
        setSearchMode={setSearchMode}
        query={activeQuery}
        setQuery={setActiveQuery}
        filters={filters}
        setFilters={setFilters}
        loading={loading}
        error={error}
        retryAdvised={retryAdvised}
        retrySearch={retrySearch}
        visibleResults={visibleResults}
        addExpert={handleAdd}
        atMax={atMax}
        onClose={() => setViewMode("simplified")}
      />
    </div>
  );
}
